// Multilingual Israeli-city matching (he / ru / en / fr).
//
// Why: masters store their city inconsistently — from the settings picker it's
// English ("Netanya"), but legacy / custom entries are free text in Hebrew
// ("נתניה") or Russian ("Нетания"). Meanwhile the search box and the YofiBot
// assistant receive the city in the USER'S language. A plain substring match
// ("нетания" in "Netanya") therefore fails and hides every master. This maps
// all language variants of the major cities to one group so matching works
// across languages. Same-language substring matching still works for any city
// not in the list (custom towns), so nothing regresses.

const CITY_ALIASES: string[][] = [
  ['tel aviv', 'tel aviv-yafo', 'tel-aviv', 'тель-авив', 'тель авив', 'תל אביב', 'תל־אביב', 'תל אביב-יפו', 'תל אביב יפו'],
  ['jerusalem', 'иерусалим', 'ירושלים'],
  ['haifa', 'хайфа', 'חיפה'],
  ['netanya', 'нетания', 'натания', 'נתניה'],
  ['beer sheva', "be'er sheva", 'беэр-шева', 'беэр шева', 'באר שבע'],
  ['rishon lezion', 'rishon', 'ришон-лецион', 'ришон лецион', 'ришон', 'ראשון לציון'],
  ['ashdod', 'ашдод', 'אשדוד'],
  ['petah tikva', 'petach tikva', 'петах-тиква', 'петах тиква', 'פתח תקווה', 'פתח תקוה'],
  ['bat yam', 'бат-ям', 'бат ям', 'בת ים'],
  ['holon', 'холон', 'חולון'],
  ['herzliya', 'герцлия', 'герцелия', 'הרצליה'],
  ['ramat gan', 'рамат-ган', 'рамат ган', 'רמת גן'],
  ['eilat', 'эйлат', 'אילת'],
  ['rehovot', 'реховот', 'רחובות'],
  ['ashkelon', 'ашкелон', 'אשקלון'],
];

function norm(s: string | undefined | null): string {
  return (s || '').toLowerCase().trim();
}

/**
 * True if a master's city/address matches the query city, tolerant across
 * languages. Empty query matches everything (no city filter applied).
 */
export function cityMatches(masterCity: string | undefined | null, query: string | undefined | null): boolean {
  const m = norm(masterCity);
  const q = norm(query);
  if (!q) return true;
  if (!m) return false;
  // Same-language / direct substring (keeps working for custom towns).
  if (m.includes(q) || q.includes(m)) return true;
  // Cross-language: both sides resolve to the same alias group.
  for (const group of CITY_ALIASES) {
    const masterInGroup = group.some((a) => m.includes(a) || a.includes(m));
    const queryInGroup = group.some((a) => q.includes(a) || a.includes(q));
    if (masterInGroup && queryInGroup) return true;
  }
  return false;
}

/**
 * K-0069: город в языке интерфейса.
 *
 * В русской версии профиля заголовок вкладки выходил как «мастера красоты
 * в נתניה», а в описании рядом стояло «в Нетании» — два написания в одном
 * экране, причём первое уходит в Google и в превью ссылки WhatsApp.
 * Таблица псевдонимов выше уже знает все написания города; здесь мы просто
 * выбираем из неё то, что читается на нужном языке.
 *
 * Города вне таблицы (небольшие посёлки, произвольный ввод) возвращаются как
 * есть — потерять название хуже, чем показать его на языке владельца.
 */
const CITY_DISPLAY: Record<string, { he: string; ru: string; en: string; fr: string }> = {
  'tel aviv':      { he: 'תל אביב',    ru: 'Тель-Авив',   en: 'Tel Aviv',      fr: 'Tel Aviv' },
  jerusalem:       { he: 'ירושלים',    ru: 'Иерусалим',   en: 'Jerusalem',     fr: 'Jérusalem' },
  haifa:           { he: 'חיפה',       ru: 'Хайфа',       en: 'Haifa',         fr: 'Haïfa' },
  netanya:         { he: 'נתניה',      ru: 'Нетания',     en: 'Netanya',       fr: 'Netanya' },
  'beer sheva':    { he: 'באר שבע',    ru: 'Беэр-Шева',   en: 'Beer Sheva',    fr: 'Beer Sheva' },
  'rishon lezion': { he: 'ראשון לציון', ru: 'Ришон-ле-Цион', en: 'Rishon LeZion', fr: 'Rishon LeZion' },
  ashdod:          { he: 'אשדוד',      ru: 'Ашдод',       en: 'Ashdod',        fr: 'Ashdod' },
  'petah tikva':   { he: 'פתח תקווה',  ru: 'Петах-Тиква', en: 'Petah Tikva',   fr: 'Petah Tikva' },
  'bat yam':       { he: 'בת ים',      ru: 'Бат-Ям',      en: 'Bat Yam',       fr: 'Bat Yam' },
  holon:           { he: 'חולון',      ru: 'Холон',       en: 'Holon',         fr: 'Holon' },
  herzliya:        { he: 'הרצליה',     ru: 'Герцлия',     en: 'Herzliya',      fr: 'Herzliya' },
  'ramat gan':     { he: 'רמת גן',     ru: 'Рамат-Ган',   en: 'Ramat Gan',     fr: 'Ramat Gan' },
  eilat:           { he: 'אילת',       ru: 'Эйлат',       en: 'Eilat',         fr: 'Eilat' },
  rehovot:         { he: 'רחובות',     ru: 'Реховот',     en: 'Rehovot',       fr: 'Rehovot' },
  ashkelon:        { he: 'אשקלון',     ru: 'Ашкелон',     en: 'Ashkelon',      fr: 'Ashkelon' },
};

export function cityLabel(city: string | undefined | null, lang: string | undefined): string {
  const raw = (city || '').trim();
  if (!raw) return '';
  const key = norm(raw);
  const group = CITY_ALIASES.find(g => g.includes(key));
  const canonical = group ? group[0] : key;
  const row = CITY_DISPLAY[canonical];
  if (!row) return raw;                       // не наш город — как ввели
  const l = (lang || 'he').slice(0, 2) as keyof typeof row;
  return row[l] || row.he;
}

/**
 * K-0069: город в предложном падеже для русского заголовка
 * («мастера красоты в Нетании», а не «в Нетания»).
 *
 * Полноценная морфология тут не нужна: список городов закрыт, а правил всего
 * три — «-ия» → «-ии», «-а» → «-е», согласная → «-е». Для остальных языков
 * форма не меняется.
 */
export function cityIn(city: string | undefined | null, lang: string | undefined): string {
  const label = cityLabel(city, lang);
  if (!label || (lang || '').slice(0, 2) !== 'ru') return label;
  if (/ия$/.test(label)) return label.replace(/ия$/, 'ии');
  if (/[ая]$/.test(label)) return label.replace(/[ая]$/, 'е');
  if (/[бвгдджзйклмнпрстфхцчшщ]$/i.test(label)) return label + 'е';
  return label;
}
